import React from 'react';
import {Plugins, Atom, type AttributesMap} from '@packages/systems/wfdl';
import {iconName, typeName, qualify} from '@packages/systems/wfdl-Qualified';
import {createIntegratedRootAliasInstance} from '@packages/utilities/integrated-types';
import {reportBug} from '@packages/utilities/reporter';
import {Map} from 'immutable';
import {useWidgetConfig} from '@packages/shared/helpers/WidgetHelper';
import {usePreviewMode, useRuntimeEnvironment} from '@packages/systems/render';
import {
  EvaluatedNodeData,
  getChildren,
  getInPojo,
} from '@packages/utilities/data-utils';
import {NONE_TAB_ID} from './constants';

const getDataTabs = (key: string, data: EvaluatedNodeData) =>
  getInPojo(data, ['tabs', key]);

// We use `data.tabs` when it's available, but if it's not, we fallback onto
// the `attributes` map (which is pre-populated from `data.attr` by the
// renderer's createBaseAttributeMap before it reaches this component).
// Note: `data.attr` is NOT available on the fixed/typed EvaluatedNodeData
// because TabsWrapperType does not declare an `attr` field. The renderer
// extracts attr into the `attributes` Immutable Map before passing to atoms.
const getDataOrAttr = (
  dataName: string,
  attrName: string,
  defaultValue: string,
  data: EvaluatedNodeData,
  attributes: AttributesMap
) => {
  const tabsVal = getDataTabs(dataName, data);
  if (tabsVal !== undefined) return tabsVal;
  const attrVal = attributes.get(attrName);
  if (attrVal !== undefined) return attrVal;

  reportBug(
    'PlatformTabsWrapperDataOrAttrError',
    'TabsWrapper has an undefined property on either data.tabs or data.attr.',
    {
      data,
      dataName,
      team: 'platform',
      ticket: 'https://webflow.atlassian.net/browse/PLAT-1011',
    }
  );

  return defaultValue;
};

const className = 'w-tabs';

const TabsWrapper = Atom.createRender(
  ({attributes, classes, context, data, generateAttributes, renderChild}) => {
    const runtimeEnvironment = useRuntimeEnvironment();
    const previewMode = usePreviewMode();
    const nativeNodeRef = useWidgetConfig(
      className,
      runtimeEnvironment,
      previewMode
    );

    // Capture the persisted default once on mount. Without this, if User A
    // changes the default to Tab 2, User B's canvas would switch to Tab 2
    // even though they didn't click anything. useState ensures the default
    // is read once and doesn't change when other sessions update it.
    const [initialDefault] = React.useState(() => {
      const val = getDataOrAttr(
        'current',
        'data-current',
        '',
        data,
        attributes
      );
      return val === NONE_TAB_ID ? '' : val;
    });

    // Session-local tab selection (written by selectTab / handleNodeClicked).
    // Falls back to whichever default was active when this session loaded.
    const tempCurrent = getInPojo(data, ['temp', 'tabs', 'current']) as
      | string
      | undefined;
    const current =
      tempCurrent === NONE_TAB_ID ? '' : tempCurrent || initialDefault;
    const easing = getDataOrAttr(
      'easing',
      'data-easing',
      'ease',
      data,
      attributes
    );
    const fadeIn = getDataOrAttr(
      'fadeIn',
      'data-duration-in',
      '300',
      data,
      attributes
    );
    const fadeOut = getDataOrAttr(
      'fadeOut',
      'data-duration-out',
      '100',
      data,
      attributes
    );
    const dataAttributes = Map({
      'data-current': current,
      'data-easing': easing,
      // @ts-expect-error - TS18047 - 'fadeIn' is possibly 'null'.
      'data-duration-in': fadeIn.toString(),
      // @ts-expect-error - TS18047 - 'fadeOut' is possibly 'null'.
      'data-duration-out': fadeOut.toString(),
    });
    // @ts-expect-error - TS2769 - No overload matches this call.
    const newAttributes = attributes.merge(dataAttributes);
    // TODO derive `data-duration-*` and `data-easing` attributes from node data.
    // Right now they are read from node.data.attr by EvalElement but that is reading from derived state.
    // `node.data.tabs` should be the source of truth.
    const updatedContext = context.set('current', current);
    const tabsWrapperClasses = classes.push(className);

    return (
      <div
        ref={nativeNodeRef}
        {...generateAttributes(newAttributes, tabsWrapperClasses)}
      >
        {getChildren(data).map((child: EvaluatedNodeData) =>
          renderChild(child, updatedContext)
        )}
      </div>
    );
  }
);

const config = Atom.create({
  displayName: 'Tabs',
  showSettingsInDescendants: true,
  constraints: {
    children: [
      {
        is: ['TabsMenu', 'TabsContent'],
        name: 'Non-Tab Item',
        rule: 'RequireOnly',
      },
    ],
  },
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Tabs'), iconName('Tabs')),
  },
  showEmpty: true,
  render: TabsWrapper,
  dataType: createIntegratedRootAliasInstance(
    qualify(Plugins.name('Tabs'), typeName('TabsWrapper'))
  ),
});

export default config;
